import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function AddRulesForm({ formData, updateFormData }) {
  const { t } = useTranslation();
  const [showIndividualRules, setShowIndividualRules] = useState(false);
  const { participants, rules, inclusions, eventType, familyUnits, showOnlyGivers, adultsGiveToAdults, preventReciprocal, preventMultiplePerFamily } = formData;

  const getFamilyUnit = (participantName) => {
    for (const unitName in familyUnits) {
      if (familyUnits[unitName].some(p => p.name === participantName)) {
        return unitName;
      }
    }
    return null;
  };

  const handleFamilyCellClick = (unit1Name, unit2Name) => {
    if (unit1Name === unit2Name) return;
    const unit1Members = familyUnits[unit1Name];
    const unit2Members = familyUnits[unit2Name];
    const newRules = JSON.parse(JSON.stringify(rules));

    const ruleExists = unit1Members.every(m1 => 
      unit2Members.every(m2 => 
        newRules[m1.name] && newRules[m1.name][m2.name] === 'two-way'
      )
    );

    unit1Members.forEach(member1 => {
      unit2Members.forEach(member2 => {
        if (member1.id !== member2.id) {
          if (!newRules[member1.name]) newRules[member1.name] = {};
          if (!newRules[member2.name]) newRules[member2.name] = {};

          if (ruleExists) {
            delete newRules[member1.name][member2.name];
            delete newRules[member2.name][member1.name];
          } else {
            newRules[member1.name][member2.name] = 'two-way';
            newRules[member2.name][member1.name] = 'two-way';
          }
        }
      });
    });

    updateFormData({ rules: newRules });
  };

  const getFamilyExclusionState = (unit1Name, unit2Name) => {
    const unit1Members = familyUnits[unit1Name];
    const unit2Members = familyUnits[unit2Name];
    if (!unit1Members || !unit2Members || unit1Members.length === 0 || unit2Members.length === 0) return false;

    return unit1Members.every(m1 => 
      unit2Members.every(m2 => 
        rules[m1.name] && rules[m1.name][m2.name] === 'two-way'
      )
    );
  };

  const getFamilyHoverText = (isExcluded, unit1Name, unit2Name) => {
    const groupType = eventType === 'kris-kindle' ? 'family' : 'group';
    if (unit1Name === unit2Name) {
      return t(`rules.familyExclusions.hoverText.cannotExcludeItself.${groupType}`);
    }
    if (isExcluded) {
      return t('rules.familyExclusions.hoverText.twoWayExclusion', { unit1: unit1Name, unit2: unit2Name });
    } else {
      return t('rules.familyExclusions.hoverText.noExclusion', { unit1: unit1Name, unit2: unit2Name });
    }
  };

  const validParticipants = participants.filter(p => p.name && p.name.trim() !== '');
  const sortedParticipants = [...validParticipants].sort((a, b) => a.name.localeCompare(b.name));

  const getExclusionType = (giver, receiver) => {
    if (!rules || !giver || !receiver) return 'none';
    const giverName = giver.name;
    const receiverName = receiver.name;

    const giverToReceiver = rules[giverName] && rules[giverName][receiverName];
    const receiverToGiver = rules[receiverName] && rules[receiverName][giverName];

    if (giverToReceiver === 'two-way' || receiverToGiver === 'two-way') {
      return 'two-way';
    }
    if (giverToReceiver === 'one-way') {
      return 'one-way';
    }
    if (receiverToGiver === 'one-way') {
      return 'one-way-reverse';
    }

    const giverFamily = getFamilyUnit(giverName);
    const receiverFamily = getFamilyUnit(receiverName);
    if (giverFamily && giverFamily === receiverFamily && giverName !== receiverName) {
      return 'two-way';
    }

    return 'none';
  };

  const handleCellClick = (giver, receiver) => {
    if (giver.name === receiver.name) return;

    const newRules = JSON.parse(JSON.stringify(rules));
    const giverName = giver.name;
    const receiverName = receiver.name;

    if (!newRules[giverName]) newRules[giverName] = {};
    if (!newRules[receiverName]) newRules[receiverName] = {};

    const currentExclusion = getExclusionType(giver, receiver);

    switch (currentExclusion) {
      case 'none':
        newRules[giverName][receiverName] = 'one-way';
        break;
      case 'family-default':
        newRules[giverName][receiverName] = 'two-way';
        newRules[receiverName][giverName] = 'two-way';
        break;
      case 'one-way':
        newRules[giverName][receiverName] = 'two-way';
        newRules[receiverName][giverName] = 'two-way';
        break;
      case 'two-way':
        delete newRules[giverName][receiverName];
        delete newRules[receiverName][giverName];
        break;
      case 'one-way-reverse':
        delete newRules[receiverName][giverName];
        break;
      default:
        break;
    }
    updateFormData({ rules: newRules });
  };

  const getInclusionType = (giver, receiver) => {
    if (!inclusions || !giver || !receiver) return 'none';
    const giverName = giver.name;
    const receiverName = receiver.name;

    const giverToReceiver = inclusions[giverName] && inclusions[giverName][receiverName];

    if (giverToReceiver) {
      return 'one-way';
    }

    return 'none';
  };

  const handleInclusionCellClick = (giver, receiver) => {
    if (giver.name === receiver.name) return;

    const newInclusions = JSON.parse(JSON.stringify(inclusions));
    const giverName = giver.name;
    const receiverName = receiver.name;

    if (!newInclusions[giverName]) newInclusions[giverName] = {};

    const currentInclusion = getInclusionType(giver, receiver);

    if (currentInclusion === 'none') {
      newInclusions[giverName][receiverName] = true;
    } else {
      delete newInclusions[giverName][receiverName];
    }

    updateFormData({ inclusions: newInclusions });
  };

  const getInclusionHoverText = (inclusionType, giver, receiver) => {
    const giverName = giver.name;
    const receiverName = receiver.name;

    if (inclusionType === 'one-way') {
      return t('rules.inclusionRules.hoverText.oneWay', { giver: giverName, receiver: receiverName });
    } else {
      return t('rules.inclusionRules.hoverText.noInclusion');
    }
  };

  const getHoverText = (exclusionType, giver, receiver) => {
    const giverName = giver.name;
    const receiverName = receiver.name;

    switch (exclusionType) {
      case 'one-way':
        return t('rules.individualExclusions.hoverText.oneWay', { giver: giverName, receiver: receiverName });
      case 'two-way':
        return t('rules.individualExclusions.hoverText.twoWay', { giver: giverName, receiver: receiverName });
      case 'one-way-reverse':
        return t('rules.individualExclusions.hoverText.oneWayReverse', { giver: giverName, receiver: receiverName });
      case 'family-default':
        return t('rules.individualExclusions.hoverText.familyDefault', { giver: giverName, receiver: receiverName });
      case 'none':
      default:
        return t('rules.individualExclusions.hoverText.noExclusion', { giver: giverName, receiver: receiverName });
    }
  };

  const familyUnitNames = Object.keys(familyUnits);
  const givers = showOnlyGivers ? sortedParticipants.filter(p => p.isGiver) : sortedParticipants;

  return (
    <div className="space-y-6">
      {/* Options Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('rules.assignmentOptions.title')}</h3>
        <div className="space-y-3">
          {eventType === 'kris-kindle' && (
            <>
              <div className="flex items-start p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  id="showOnlyGivers"
                  checked={showOnlyGivers}
                  onChange={(e) => updateFormData({ showOnlyGivers: e.target.checked })}
                  className="w-4 h-4 text-blue-600 mt-0.5 mr-3"
                />
                <label htmlFor="showOnlyGivers" className="text-sm font-medium text-gray-700 cursor-pointer">
                  {t('rules.assignmentOptions.showOnlyGivers')}
                </label>
              </div>
              <div className="flex items-start p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  id="adultsGiveToAdults"
                  checked={adultsGiveToAdults}
                  onChange={(e) => updateFormData({ adultsGiveToAdults: e.target.checked })}
                  className="w-4 h-4 text-blue-600 mt-0.5 mr-3"
                />
                <label htmlFor="adultsGiveToAdults" className="text-sm font-medium text-gray-700 cursor-pointer">
                  {t('rules.assignmentOptions.adultsGiveToAdults')}
                </label>
              </div>
            </>
          )}
          <div className="flex items-start p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              id="preventReciprocal"
              checked={preventReciprocal}
              onChange={(e) => updateFormData({ preventReciprocal: e.target.checked })}
              className="w-4 h-4 text-blue-600 mt-0.5 mr-3"
            />
            <label htmlFor="preventReciprocal" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
              {t('rules.assignmentOptions.preventReciprocal')}
            </label>
          </div>
          <div className="flex items-start p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              id="preventMultiplePerFamily"
              checked={preventMultiplePerFamily}
              onChange={(e) => updateFormData({ preventMultiplePerFamily: e.target.checked })}
              className="w-4 h-4 text-blue-600 mt-0.5 mr-3"
            />
            <label htmlFor="preventMultiplePerFamily" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
              {eventType === 'kris-kindle'
                ? t('rules.assignmentOptions.preventMultiplePerFamily.krisKindle')
                : t('rules.assignmentOptions.preventMultiplePerFamily.secretSanta')}
            </label>
          </div>
        </div>
      </div>

      {/* Family Unit / Team / Classroom Exclusions Section */}
      {familyUnitNames.length > 0 && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {eventType === 'kris-kindle'
              ? t('rules.familyExclusions.title.krisKindle')
              : t('rules.familyExclusions.title.secretSanta')}
          </h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-base font-semibold text-blue-900 mb-3">{t('rules.familyExclusions.legend.title')}</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <span className="font-bold text-xl mr-3 text-gray-600">—</span>
                <span className="text-sm text-blue-900">{t('rules.familyExclusions.legend.noExclusion')}</span>
              </div>
              <div className="flex items-center">
                <span className="font-bold text-xl mr-3">❌</span>
                <span className="text-sm text-blue-900">{t('rules.familyExclusions.legend.twoWayExclusion')}</span>
              </div>
            </div>
            <p className="text-xs text-blue-800 mt-3">
              {eventType === 'kris-kindle'
                ? t('rules.familyExclusions.legend.tip.krisKindle')
                : t('rules.familyExclusions.legend.tip.secretSanta')}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-2 border-gray-300 table-fixed rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b-2 border-gray-300 w-40 font-bold sticky left-0 bg-gray-100 z-20 text-gray-800" rowSpan="2">{t('rules.familyExclusions.givers')}</th>
                  <th className="py-3 px-4 border-b-2 border-gray-300 text-center font-bold text-gray-800" colSpan={familyUnitNames.length}>{t('rules.familyExclusions.receivers')}</th>
                </tr>
                <tr>
                  {familyUnitNames.map(unitName => (
                    <th key={unitName} className="py-2 px-2 border-b-2 border-gray-300 text-center align-bottom h-24 w-24 bg-gray-100">
                      <div className="flex items-center justify-center h-full">
                        <span className="transform -rotate-90 whitespace-nowrap text-sm font-semibold text-gray-700">
                          {unitName}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {familyUnitNames.map(unit1Name => (
                  <tr key={unit1Name} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold sticky left-0 bg-white z-10 text-gray-800">{unit1Name}</td>
                    {familyUnitNames.map(unit2Name => {
                      const isSelf = unit1Name === unit2Name;
                      const isExcluded = getFamilyExclusionState(unit1Name, unit2Name);
                      return (
                        <td
                          key={unit2Name}
                          className={`py-3 px-4 border-b border-gray-200 text-center text-xl transition-colors ${
                            isSelf
                              ? 'bg-gray-200 cursor-not-allowed'
                              : isExcluded
                                ? 'bg-red-100 hover:bg-red-200 cursor-pointer'
                                : 'hover:bg-blue-50 cursor-pointer'
                          }`}
                          onClick={() => handleFamilyCellClick(unit1Name, unit2Name)}
                          title={getFamilyHoverText(isExcluded, unit1Name, unit2Name)}
                        >
                          {isSelf ? '' : isExcluded ? '❌' : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Advanced Toggle Button */}
      {familyUnitNames.length > 0 ? (
        <div className="flex justify-center">
          <button
            onClick={() => setShowIndividualRules(!showIndividualRules)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-6 rounded-lg border border-gray-300 transition-colors shadow-sm"
          >
            {showIndividualRules ? t('rules.advancedRules.hideButton') : t('rules.advancedRules.showButton')}
          </button>
        </div>
      ) : null}

      {/* Individual Exclusion Rules Section */}
      {(showIndividualRules || familyUnitNames.length === 0) && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{t('rules.individualExclusions.title')}</h3>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <h4 className="text-base font-semibold text-orange-900 mb-3">{t('rules.individualExclusions.legend.title')}</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2 text-gray-600">—</span>
                <span className="text-sm text-orange-900">{t('rules.individualExclusions.legend.noExclusion')}</span>
              </div>
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2">→</span>
                <span className="text-sm text-orange-900">{t('rules.individualExclusions.legend.oneWay')}</span>
              </div>
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2">←</span>
                <span className="text-sm text-orange-900">{t('rules.individualExclusions.legend.reverse')}</span>
              </div>
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2">❌</span>
                <span className="text-sm text-orange-900">{t('rules.individualExclusions.legend.twoWay')}</span>
              </div>
            </div>
            <p className="text-xs text-orange-800 mt-3">{t('rules.individualExclusions.legend.tip')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-2 border-gray-300 table-fixed rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b-2 border-gray-300 w-40 font-bold sticky left-0 bg-gray-100 z-20 text-gray-800" rowSpan="2">{t('rules.individualExclusions.givers')}</th>
                  <th className="py-3 px-4 border-b-2 border-gray-300 text-center font-bold text-gray-800" colSpan={sortedParticipants.length}>
                    {t('rules.individualExclusions.receivers')}
                  </th>
                </tr>
                <tr>
                  {sortedParticipants.map((p, i) => (
                    <th key={p.name + i} className="py-2 px-2 border-b-2 border-gray-300 text-center align-bottom h-24 w-24 bg-gray-100">
                      <div className="flex items-center justify-center h-full">
                        <span className="transform -rotate-90 whitespace-nowrap text-sm font-semibold text-gray-700">
                          {p.name}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {givers.map((giver, giverIndex) => (
                  <tr key={giver.name + giverIndex} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold sticky left-0 bg-white z-10 text-gray-800">{giver.name}</td>
                    {sortedParticipants.map((receiver, receiverIndex) => {
                      const isSelf = giver.name === receiver.name;
                      const exclusionType = getExclusionType(giver, receiver);
                      let cellClasses = `py-3 px-4 border-b border-gray-200 text-center text-xl transition-colors`;

                      if (isSelf) {
                        cellClasses += ' bg-gray-200 cursor-not-allowed';
                      } else if (exclusionType === 'two-way' || exclusionType === 'family-default') {
                        cellClasses += ' bg-red-100 hover:bg-red-200 cursor-pointer';
                      } else if (exclusionType === 'one-way') {
                        cellClasses += ' bg-orange-100 hover:bg-orange-200 cursor-pointer';
                      } else if (exclusionType === 'one-way-reverse') {
                        cellClasses += ' bg-orange-50 hover:bg-orange-100 cursor-pointer';
                      } else {
                        cellClasses += ' hover:bg-blue-50 cursor-pointer';
                      }

                      return (
                        <td
                          key={receiver.name + receiverIndex}
                          className={cellClasses}
                          onClick={isSelf ? undefined : () => handleCellClick(giver, receiver)}
                          title={isSelf ? t('rules.individualExclusions.cannotGiftSelf') : getHoverText(exclusionType, giver, receiver)}
                        >
                          {isSelf ? '' : (exclusionType === 'two-way' || exclusionType === 'family-default') ? '❌' : exclusionType === 'one-way' ? '→' : exclusionType === 'one-way-reverse' ? '←' : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Individual Inclusion Rules Section */}
      {(showIndividualRules || familyUnitNames.length === 0) && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{t('rules.inclusionRules.title')}</h3>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h4 className="text-base font-semibold text-green-900 mb-3">{t('rules.inclusionRules.legend.title')}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2 text-gray-600">—</span>
                <span className="text-sm text-green-900">{t('rules.inclusionRules.legend.noInclusion')}</span>
              </div>
              <div className="flex items-center">
                <span className="font-bold text-xl mr-2">→</span>
                <span className="text-sm text-green-900">{t('rules.inclusionRules.legend.oneWay')}</span>
              </div>
            </div>
            <p className="text-xs text-green-800 mt-3">{t('rules.inclusionRules.legend.tip')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-2 border-gray-300 table-fixed rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-4 border-b-2 border-gray-300 w-40 font-bold sticky left-0 bg-gray-100 z-20 text-gray-800" rowSpan="2">{t('rules.inclusionRules.givers')}</th>
                  <th className="py-3 px-4 border-b-2 border-gray-300 text-center font-bold text-gray-800" colSpan={sortedParticipants.length}>
                    {t('rules.inclusionRules.receivers')}
                  </th>
                </tr>
                <tr>
                  {sortedParticipants.map((p, i) => (
                    <th key={p.name + i} className="py-2 px-2 border-b-2 border-gray-300 text-center align-bottom h-24 w-24 bg-gray-100">
                      <div className="flex items-center justify-center h-full">
                        <span className="transform -rotate-90 whitespace-nowrap text-sm font-semibold text-gray-700">
                          {p.name}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {givers.map((giver, giverIndex) => (
                  <tr key={giver.name + giverIndex} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold sticky left-0 bg-white z-10 text-gray-800">{giver.name}</td>
                    {sortedParticipants.map((receiver, receiverIndex) => {
                      const isSelf = giver.name === receiver.name;
                      const inclusionType = getInclusionType(giver, receiver);
                      let cellClasses = `py-3 px-4 border-b border-gray-200 text-center text-xl transition-colors`;

                      if (isSelf) {
                        cellClasses += ' bg-gray-200 cursor-not-allowed';
                      } else if (inclusionType === 'one-way') {
                        cellClasses += ' bg-green-100 hover:bg-green-200 cursor-pointer';
                      } else {
                        cellClasses += ' hover:bg-blue-50 cursor-pointer';
                      }

                      return (
                        <td
                          key={receiver.name + receiverIndex}
                          className={cellClasses}
                          onClick={isSelf ? undefined : () => handleInclusionCellClick(giver, receiver)}
                          title={isSelf ? t('rules.inclusionRules.cannotIncludeSelf') : getInclusionHoverText(inclusionType, giver, receiver)}
                        >
                          {isSelf ? '' : inclusionType === 'one-way' ? '→' : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddRulesForm;
